import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { Button, BackButton, InlineSpinner, useToast } from '../../components/ui';

function ContentGeneratorGoogle() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [googleTrends, setGoogleTrends] = useState([]);
  const [isLoadingTrends, setIsLoadingTrends] = useState(false);
  const [selectedTrend, setSelectedTrend] = useState(null);
  const [selectedNewsIndex, setSelectedNewsIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchGoogleTrends();
  }, []);

  const fetchGoogleTrends = async () => {
    setIsLoadingTrends(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/google-trends/trending?geo=TW&maxItems=10', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setGoogleTrends(data.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch Google Trends:', error);
      toast.error('無法載入趨勢資料');
    } finally {
      setIsLoadingTrends(false);
    }
  };

  const handleContinue = async () => {
    if (!selectedTrend && !searchQuery.trim()) {
      toast.warning('Please select a trend or enter a topic');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const topic = selectedTrend?.keyword || searchQuery;

      const projectData = {
        type: 'content-generator',
        source: 'google-trends',
        currentStep: 2,
        keywords: [topic],
        topic: topic,
        pageCount: 5,
        contentType: 'carousel',
        language: 'zh-TW',
        preset: 'default',
        generationStatus: 'draft'
      };

      if (selectedTrend) {
        projectData.selectedTrend = selectedTrend;
        if (selectedNewsIndex !== null && selectedTrend.news?.[selectedNewsIndex]) {
          projectData.referenceNews = selectedTrend.news[selectedNewsIndex];
        }
      }

      const response = await fetch('/api/projects/create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(projectData)
      });

      const result = await response.json();
      if (!result.success) {
        toast.error('Failed to create project');
        return;
      }

      navigate(`/templates/content-generator/step2?projectId=${result.project.id}`);
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error('Failed to create project');
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-8">
      {/* Home Button */}
      <Button
        onClick={() => navigate('/home')}
        variant="primary"
        size="md"
        icon="mdi:home"
        className="fixed top-8 left-8 z-[100]"
      >
        Home
      </Button>

      {/* Back Button */}
      <BackButton
        onClick={() => navigate('/templates/content-generator')}
        className="fixed bottom-8 left-8 z-[100]"
      >
        Back
      </BackButton>

      {/* Main Content */}
      <div className="max-w-[980px] w-full bg-white p-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto mb-4 bg-green-50 rounded-2xl flex items-center justify-center">
            <Icon icon="mdi:trending-up" className="text-4xl text-green-500" />
          </div>
          <h1 className="text-4xl font-bold text-black mb-2">Google Trends</h1>
          <p className="text-base text-gray-500">Create content from trending topics</p>
        </div>

        {/* Trends Grid */}
        <div className="max-w-[800px] mx-auto">
          <div className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
            Trending Topics
            {isLoadingTrends && <InlineSpinner size="sm" />}
            <button
              onClick={fetchGoogleTrends}
              disabled={isLoadingTrends}
              className="ml-auto text-xs text-slate-500 hover:text-black flex items-center gap-1 cursor-pointer bg-transparent border-none"
            >
              <Icon icon="mdi:refresh" className={isLoadingTrends ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          {googleTrends.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 mb-6">
              {googleTrends.map((trend, index) => (
                <div
                  key={index}
                  onClick={() => {
                    setSelectedTrend(trend);
                    setSearchQuery(trend.keyword || trend.title || trend.query || trend);
                    setSelectedNewsIndex(null);
                  }}
                  className={`cursor-pointer p-4 rounded-lg border transition-all hover:border-black hover:bg-white hover:-translate-y-0.5 ${
                    selectedTrend?.keyword === trend.keyword
                      ? 'bg-green-50 border-green-500'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="text-sm font-semibold text-slate-800 mb-1">
                    {trend.keyword || trend.title || trend.query || trend}
                  </div>
                  <div className="text-[11px] text-slate-500 flex gap-3 items-center">
                    {trend.searchVolume && <span>{trend.searchVolume}</span>}
                    {trend.growth && <span className="text-green-600 font-semibold">{trend.growth}</span>}
                    {trend.startedHours !== undefined && <span>{trend.startedHours}h ago</span>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-slate-400 bg-gray-50 rounded-lg mb-6">
              {isLoadingTrends ? 'Loading...' : 'No trending data available'}
            </div>
          )}

          {/* Related News */}
          {selectedTrend && selectedTrend.news && selectedTrend.news.length > 0 && (
            <div className="mb-8">
              <div className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <span>📰 In the news</span>
                <span className="text-xs text-slate-500 font-normal">- {selectedTrend.keyword}</span>
              </div>
              <div className="flex flex-col gap-3">
                {selectedTrend.news.map((article, index) => {
                  const isSelected = selectedNewsIndex === index;
                  return (
                    <div
                      key={index}
                      onClick={() => {
                        setSelectedNewsIndex(index);
                        setSearchQuery(article.title);
                      }}
                      className={`cursor-pointer p-4 rounded-lg border flex gap-4 transition-all hover:-translate-y-0.5 ${
                        isSelected
                          ? 'bg-green-50 border-green-500 shadow-[0_0_0_3px_rgba(34,197,94,0.1)]'
                          : 'bg-white border-gray-200 hover:border-black hover:shadow-md'
                      }`}
                    >
                      {article.image && (
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-[100px] h-[100px] object-cover rounded-md shrink-0"
                        />
                      )}
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-slate-800 mb-2 leading-snug">
                          {article.title}
                        </div>
                        <div className="text-xs text-slate-500 mb-2 leading-relaxed line-clamp-2">
                          {article.snippet}
                        </div>
                        <div className="text-[11px] text-slate-400 flex gap-4">
                          <span>{article.time}</span>
                          <span>•</span>
                          <span>{article.source}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Continue Button */}
          <Button
            onClick={handleContinue}
            disabled={!selectedTrend && !searchQuery.trim()}
            variant="primary"
            size="lg"
            icon="mdi:arrow-right"
            className="w-full"
          >
            Continue to Design
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ContentGeneratorGoogle;
